import { Injectable, inject } from '@angular/core';
import { ProductInfo, FinishOptionInfo, FinishInfo } from '../models/product.model';
import { 
  OrderInfo, 
  OrderItemsGroupInfo, 
  OrderItemInfo, 
  OrderItemFileInfo,
  OrderItemFinishInfo,
  createOrderInfo,
  createOrderItemsGroupInfo,
  createOrderItemInfo,
  createOrderItemFileInfo,
  OrderItemType,
  OrderItemsGroupStatus,
  AMFProductPriceInfo
} from '../models/order.model';
import { ProjectInfo } from '../models/app.model';
import { ImageInfo } from '../models/user.model';
import { TranslateService } from '@ngx-translate/core';

// Extended ImageInfo for server files (from ServerFile)
interface ServerFileInfo extends ImageInfo {
  name?: string;
  serverName?: string;
  serverId?: number;
  pathStructureId?: number;
  size?: number;
  md5?: string;
  fileTypeId?: number;
  qty?: number;  // Quantity for this file
}

/**
 * OrderBuilderService - utility service for building order objects
 * Converted from util/OrderUtil.as
 */
@Injectable({
  providedIn: 'root'
})
export class OrderBuilderService {
  private readonly translate = inject(TranslateService);

  /**
   * Build a complete OrderInfo object for addToCart
   * This creates the full structure expected by OrderService.addToCart()
   */
  buildOrderForCart(
    product: ProductInfo,
    selectedFinishes: Map<number, any>,
    quantity: number = 1,
    orderName?: string,
    projectInfo?: ProjectInfo,
    files?: ServerFileInfo[],
    customWidth?: number,
    customHeight?: number,
    priceResult?: OrderItemInfo,
    observations?: string
  ): OrderInfo {
    const group = this.getOrderItemsGroup(
      product,
      selectedFinishes,
      quantity,
      orderName,
      projectInfo,
      files,
      customWidth,
      customHeight,
      priceResult,
      observations
    );
    
    return createOrderInfo({
      groups: [group]
    });
  }

  /**
   * Create an OrderItemsGroupInfo from a product
   * Based on OrderUtil.getOrderItemsGroup()
   */
  getOrderItemsGroup(
    product: ProductInfo,
    selectedFinishes: Map<number, any>,
    quantity: number = 1,
    orderName?: string,
    projectInfo?: ProjectInfo,
    files?: ServerFileInfo[],
    customWidth?: number,
    customHeight?: number,
    priceResult?: OrderItemInfo,
    observations?: string
  ): OrderItemsGroupInfo {
    const orderItem = this.getOrderItem(
      product,
      selectedFinishes,
      quantity,
      projectInfo,
      files,
      customWidth,
      customHeight,
      priceResult,
      undefined,  // finishOptions
      orderName   // pass draft name to item
    );
    
    return createOrderItemsGroupInfo({
      name: orderName || product.name || null,
      // code will be auto-generated by createOrderItemsGroupInfo
      observations: observations || '',
      showcase: 0,
      products: [orderItem]
    });
  }

  /**
   * Create an OrderItemInfo from a product
   * Based on OrderUtil.getOrderItem() and parseProduct()
   */
  getOrderItem(
    product: ProductInfo,
    selectedFinishes: Map<number, any>,
    quantity: number = 1,
    projectInfo?: ProjectInfo,
    files?: ServerFileInfo[],
    customWidth?: number,
    customHeight?: number,
    priceResult?: OrderItemInfo,
    finishOptions?: FinishOptionInfo[],  // Optional - use instead of product.finishOptions if provided
    orderName?: string  // Draft/project name to save in pro_order_items.name
  ): OrderItemInfo {
    // Build finishes array from selected options
    // Use provided finishOptions or fall back to product.finishOptions
    const optionsToUse = finishOptions || product.finishOptions || [];
    const finishes = this.buildFinishesArray(optionsToUse, selectedFinishes);
    
    // Build files array if provided
    const orderFiles: OrderItemFileInfo[] = [];
    if (files && files.length > 0) {
      files.forEach((file, index) => {
        const orderFile = this.createFileFromImage(file, index);
        console.log('[OrderBuilder] Created OrderItemFileInfo:', orderFile);
        orderFiles.push(orderFile);
      });
    }
    
    console.log('[OrderBuilder] Order files array:', orderFiles);
    
    // Use price result if available (from getProductPrice call)
    const prices = priceResult?.prices || null;
    const price = priceResult?.price || 0;
    const numPages = priceResult?.numPages || 0;
    const numExtraPages = priceResult?.numExtraPages || 0;
    
    return createOrderItemInfo({
      code: (product as any).ordercode || null,  // ordercode from ProductInfo
      productId: Number(product.id) || 0,
      productTypeId: Number(product.typeId) || 2,
      productSubTypeId: Number(product.subTypeId) || 0,
      subCategoryName: (product as any).subTypeName || null,  // subTypeName from ProductInfo
      productName: product.name || '',
      name: orderName || '',  // Draft/project name for pro_order_items.name
      sectorId: Number(product.sectorId) || 5,
      qty: quantity,
      width: customWidth || 0,
      height: customHeight || 0,
      finishes: finishes,
      files: orderFiles,
      type: projectInfo ? OrderItemType.PROJECT : OrderItemType.NORMAL,
      projectInfo: projectInfo || null,
      prices: prices,
      price: price,
      numPages: numPages,
      numExtraPages: numExtraPages,
      showPrice: false,
      includeInOrder: true
    });
  }

  /**
   * Build finishes array from selected finish options
   * Based on parseFinishes logic from OrderUtil.as
   * 
   * FinishOptionType mapping:
   * 1 = DROPDOWNLIST, 2 = DROPDOWNLIST_IMAGES, 3 = RADIO, 4 = CHECK
   * 5 = LIST/NUMERIC_STEPPER, 6 = NUMERIC_LIST, 7 = TABBED_DROPDOWN_IMAGES
   * 8 = DROPDOWN_IMAGES, 9 = CHECK_FILES, 10 = WIDTH_INPUT
   * 11 = HEIGHT_INPUT, 12 = TEXT_INPUT, 13 = CHECK_DVDSOCKET
   * 14 = MULTI_SELECT, 15 = COLOR_PICKER, 16 = REMOVE_LOGO
   * 17 = ENHANCE_FILES, 18 = MARGINS
   */
  buildFinishesArray(
    finishOptions: FinishOptionInfo[],
    selectedFinishes: Map<number, any>
  ): OrderItemFinishInfo[] {
    const finishes: OrderItemFinishInfo[] = [];
    
    console.log('[OrderBuilder] buildFinishesArray - finishOptions count:', finishOptions?.length || 0);
    console.log('[OrderBuilder] buildFinishesArray - selectedFinishes:', Array.from(selectedFinishes.entries()));
    
    if (!finishOptions || finishOptions.length === 0) {
      console.log('[OrderBuilder] buildFinishesArray - no finish options, returning empty');
      return finishes;
    }
    
    for (const option of finishOptions) {
      const selectedValue = selectedFinishes.get(option.id);
      
      console.log(`[OrderBuilder] Option ${option.id} "${option.name}" type ${option.type} - selectedValue:`, selectedValue);
      
      if (selectedValue === undefined || selectedValue === null) {
        continue;
      }
      
      const optionType = Number(option.type) || 1;
      
      // Handle different option types
      switch (optionType) {
        case 1:  // DROPDOWNLIST
        case 2:  // DROPDOWNLIST_IMAGES
        case 3:  // RADIO
        case 7:  // TABBED_DROPDOWN_IMAGES
        case 8:  // DROPDOWN_IMAGES
          // Single select - selectedValue is the finish id
          this.addSingleFinish(finishes, option, selectedValue);
          break;
          
        case 4:  // CHECK (selectable checkbox)
        case 13: // CHECK_DVDSOCKET
        case 16: // REMOVE_LOGO
          // Selectable checkbox - can be boolean or have a selected finish
          if (selectedValue === true) {
            // Boolean checkbox without finishes
            finishes.push({
              _explicitType: 'remote.order.OrderItemFinishInfo',
              id: 0,
              name: this.translate.instant('COMMON.YES'),
              optionId: Number(option.id),
              optionName: option.name || '',
              optionTypeId: optionType,
              data: null,
              withoutPrice: 0,
              qty: 0
            });
          } else if (typeof selectedValue === 'number' || (typeof selectedValue === 'string' && !isNaN(Number(selectedValue)))) {
            // Has a selected finish ID
            this.addSingleFinish(finishes, option, selectedValue);
          }
          break;
          
        case 5:  // LIST / NUMERIC_STEPPER
          // These are quantity selectors, NOT finish options
          // The qty value is handled by the caller (getOrderItem)
          // According to legacy parseProduct(), these should NOT be added to finishes
          // They only set orderItem.qty = int(finishOption.selectedFinish.name) or int(finishOption.value)
          break;
          
        case 6:  // NUMERIC_LIST
          // These are quantity selectors, NOT finish options
          // The qty value is handled by the caller (getOrderItem)
          // According to legacy parseProduct(), these should NOT be added to finishes
          break;
          
        case 9:  // CHECK_FILES
          // File upload toggle - files are handled separately in getOrderItem/addToCart
          // IMPORTANT: When includeInOrder == 0, do NOT add finish to the array (legacy behavior)
          // Files are still collected separately but finishOptionId should be 0
          const includeInOrderValue = option.includeInOrder;
          // Only add finish if includeInOrder is explicitly set to a non-zero value
          const shouldIncludeFinish = includeInOrderValue !== undefined && includeInOrderValue !== null && Number(includeInOrderValue) !== 0;
          console.log(`[OrderBuilder] CHECK_FILES option ${option.id}: selected=${selectedValue}, includeInOrder=${includeInOrderValue} (${typeof includeInOrderValue}), shouldIncludeFinish=${shouldIncludeFinish}`);
          if (selectedValue === true && shouldIncludeFinish) {
            if (option.finishes && option.finishes.length > 0) {
              this.addSingleFinish(finishes, option, option.finishes[0].id);
            } else {
              finishes.push({
                _explicitType: 'remote.order.OrderItemFinishInfo',
                id: 0,
                name: this.translate.instant('COMMON.YES'),
                optionId: Number(option.id),
                optionName: option.name || '',
                optionTypeId: optionType,
                data: null,
                withoutPrice: 0,
                qty: 0
              });
            }
          }
          // NOTE: If includeInOrder == 0, NO finish is added (legacy behavior)
          // Files are collected separately with finishOptionId = 0 (not the option ID)
          break;
          
        case 10: // WIDTH_INPUT
          // Width - selectedValue is the width number
          {
            const numValue = Number(selectedValue);
            finishes.push({
              _explicitType: 'remote.order.OrderItemFinishInfo',
              id: 0,
              name: String(selectedValue),
              optionId: Number(option.id),
              optionName: option.name || '',
              optionTypeId: optionType,
              data: isNaN(numValue) ? 0 : numValue,
              withoutPrice: 0,
              qty: 0
            });
          }
          break;
          
        case 11: // HEIGHT_INPUT
          // Height - selectedValue is the height number
          {
            const numValue = Number(selectedValue);
            finishes.push({
              _explicitType: 'remote.order.OrderItemFinishInfo',
              id: 0,
              name: String(selectedValue),
              optionId: Number(option.id),
              optionName: option.name || '',
              optionTypeId: optionType,
              data: isNaN(numValue) ? 0 : numValue,
              withoutPrice: 0,
              qty: 0
            });
          }
          break;
          
        case 12: { // TEXT_INPUT
          // Text input - selectedValue is the text. Skip empty / whitespace
          // so unfilled optional inputs don't pollute the cart payload with
          // `value: ""`. Mirrors the panel-pricing behaviour at
          // pricing-state.service.ts (single + all-panels paths). Required
          // text inputs are blocked earlier by the validation in
          // products-list addAlbumToCart / addToCart, so this only affects
          // optional fields.
          const text = selectedValue == null ? '' : String(selectedValue).trim();
          if (!text) break;
          finishes.push({
            _explicitType: 'remote.order.OrderItemFinishInfo',
            id: 0,
            name: text,
            optionId: Number(option.id),
            optionName: option.name || '',
            optionTypeId: optionType,
            data: text,
            withoutPrice: 0,
            qty: 0
          });
          break;
        }
          
        case 14: // MULTI_SELECT
          // Multi-select - selectedValue is an array of finish ids
          if (Array.isArray(selectedValue)) {
            for (const finishId of selectedValue) {
              this.addSingleFinish(finishes, option, finishId);
            }
          } else {
            // Could also be single select
            this.addSingleFinish(finishes, option, selectedValue);
          }
          break;
          
        case 15: // COLOR_PICKER
          // Color picker - selectedValue is the color hex or finish id
          if (option.finishes && option.finishes.length > 0) {
            this.addSingleFinish(finishes, option, selectedValue);
          } else {
            finishes.push({
              _explicitType: 'remote.order.OrderItemFinishInfo',
              id: 0,
              name: String(selectedValue),
              optionId: Number(option.id),
              optionName: option.name || '',
              optionTypeId: optionType,
              data: selectedValue,
              withoutPrice: 0,
              qty: 0
            });
          }
          break;
          
        case 17: // ENHANCE_FILES (checkbox)
        case 18: // MARGINS (checkbox)
          // Checkbox - selectedValue is boolean
          // Only add if checked (true)
          if (selectedValue === true) {
            // If there are finishes, use the first one
            const finish = option.finishes?.[0];
            if (finish) {
              finishes.push({
                _explicitType: 'remote.order.OrderItemFinishInfo',
                id: Number(finish.id),
                name: this.translate.instant('COMMON.YES'),
                optionId: Number(option.id),
                optionName: option.name || '',
                optionTypeId: optionType,
                data: null,
                withoutPrice: (finish as any).withoutPrice || 0,
                qty: 1
              });
            } else {
              // No finish defined, just add with the option info
              finishes.push({
                _explicitType: 'remote.order.OrderItemFinishInfo',
                id: 0,
                name: this.translate.instant('COMMON.YES'),
                optionId: Number(option.id),
                optionName: option.name || '',
                optionTypeId: optionType,
                data: true,
                withoutPrice: 0,
                qty: 1
              });
            }
          }
          break;
          
        default:
          // Unknown type - try to add as single select if numeric
          if (typeof selectedValue === 'number' || (typeof selectedValue === 'string' && !isNaN(Number(selectedValue)))) {
            this.addSingleFinish(finishes, option, selectedValue);
          }
          break;
      }
    }
    
    return finishes;
  }

  /**
   * Add a single finish to the array
   */
  private addSingleFinish(
    finishes: OrderItemFinishInfo[],
    option: FinishOptionInfo,
    finishId: any
  ): void {
    const finish = option.finishes?.find((f: FinishInfo) => Number(f.id) === Number(finishId));
    if (finish) {
      finishes.push({
        _explicitType: 'remote.order.OrderItemFinishInfo',
        id: Number(finish.id),
        name: finish.name || '',
        optionId: Number(option.id),
        optionName: option.name || '',
        optionTypeId: Number(option.type) || 1,
        data: null,
        withoutPrice: (finish as any).withoutPrice || 0,
        qty: 0
      });
    }
  }

  /**
   * Create an OrderItemFileInfo from an ImageInfo or ServerFileInfo
   */
  createFileFromImage(image: ServerFileInfo, index: number = 0): OrderItemFileInfo {
    console.log('[OrderBuilder] createFileFromImage - input image:', image, 'qty:', image.qty, 'finishOptionId:', (image as any).finishOptionId);
    const result = createOrderItemFileInfo({
      id: Number(image.id) || 0,
      name: image.name || null,
      serverName: image.serverName || null,
      serverId: image.serverId || 0,
      pathStructureId: image.pathStructureId || 0,
      width: image.width || 0,
      height: image.height || 0,
      size: image.size || 0,
      md5: image.md5 || null,
      fileTypeId: image.fileTypeId || 1,  // Default to JPEG (1)
      qty: image.qty || 1,  // Use quantity from file if provided
      finishOptionId: (image as any).finishOptionId || 0,  // Preserve finishOptionId from input
      editorViewIndex: index,
      isProcessedFile: false,
      processedFileUrl: null,
      deleted: false
    });
    console.log('[OrderBuilder] createFileFromImage - output qty:', result.qty, 'finishOptionId:', result.finishOptionId);
    return result;
  }

  /**
   * Create OrderInfo for multiple products (outside order)
   * Based on OrderUtil.getOutsideOrderItemsGroup()
   */
  buildOrderFromMultipleProducts(
    products: Array<{
      product: ProductInfo;
      selectedFinishes: Map<number, any>;
      quantity: number;
      files?: ServerFileInfo[];
      customWidth?: number;
      customHeight?: number;
      priceResult?: OrderItemInfo;
    }>,
    orderName?: string
  ): OrderInfo {
    const orderItems: OrderItemInfo[] = [];
    
    for (const item of products) {
      const orderItem = this.getOrderItem(
        item.product,
        item.selectedFinishes,
        item.quantity,
        undefined,  // no projectInfo for outside orders
        item.files,
        item.customWidth,
        item.customHeight,
        item.priceResult
      );
      orderItems.push(orderItem);
    }
    
    const group = createOrderItemsGroupInfo({
      name: orderName || null,
      code: null,
      observations: '',
      showcase: 0,
      products: orderItems
    });
    
    return createOrderInfo({
      groups: [group]
    });
  }

  /**
   * Merge price result into order item
   * Updates the order item with prices from getProductPrice result
   */
  applyPriceResult(orderItem: OrderItemInfo, priceResult: any): void {
    if (priceResult && typeof priceResult === 'object') {
      if (priceResult.prices) {
        orderItem.prices = priceResult.prices;
      }
      if (typeof priceResult.price === 'number') {
        orderItem.price = priceResult.price;
      }
    }
  }
}
